// Gamification Service - Driving Theory Learning System
// Implements Daily Study Streaks and Knowledge Points/Levels for user engagement

import { logger } from '../utils/logger';

export interface GamificationData {
  knowledgePoints: number; // Current session knowledge points
  level: number;
  dailyStudyStreak: number;
  lastActivityDate: string; // ISO date string
  totalKnowledgePoints: number; // Lifetime knowledge points (for level calculation)
  streakFreezes: number; // Number of streak freezes available
  streakFrozen: boolean; // Whether streak is currently frozen
  streakProtected: boolean; // Whether streak was protected (1-day gap used)
}

export interface KnowledgePointsTransaction {
  type: 'correct_answer' | 'practice_test' | 'mock_exam' | 'daily_streak_bonus' | 'level_up';
  amount: number;
  timestamp: string;
  description: string;
}

class GamificationService {
  private readonly STORAGE_KEY = 'gamification_data';
  private readonly KP_HISTORY_KEY = 'knowledge_points_history';
  
  // Knowledge Points Values (Driving Theory Learning System)
  private readonly KP_VALUES = {
    correct_answer: 10,
    practice_test: 50,
    mock_exam: 100,
    daily_streak_bonus: 25,
    level_up: 0 // No KP for leveling up, just milestone
  };
  
  // Level thresholds (Knowledge Points required per level)
  // Level 1: 0-99, Level 2: 100-249, Level 3: 250-499, etc.
  private readonly LEVEL_THRESHOLDS = [
    0,      // Level 1
    100,    // Level 2
    250,    // Level 3
    500,    // Level 4
    1000,   // Level 5
    2000,   // Level 6
    3500,   // Level 7
    5500,   // Level 8
    8000,   // Level 9
    12000,  // Level 10
    17000,  // Level 11
    23000,  // Level 12
    30000,  // Level 13
    40000,  // Level 14
    55000,  // Level 15
    75000,  // Level 16
    100000, // Level 17
    130000, // Level 18
    170000, // Level 19
    220000  // Level 20
  ];

  /**
   * Initialize gamification data for new users
   */
  initialize(): GamificationData {
    const existing = this.loadData();
    if (existing) {
      return existing;
    }

    const newData: GamificationData = {
      knowledgePoints: 0,
      level: 1,
      dailyStudyStreak: 0,
      lastActivityDate: new Date().toISOString().split('T')[0],
      totalKnowledgePoints: 0,
      streakFreezes: 1, // Start with 1 free freeze
      streakFrozen: false,
      streakProtected: false
    };

    this.saveData(newData);
    return newData;
  }

  /**
   * Get current gamification data
   * Note: Does not update streak automatically (use recordActivity() or awardKnowledgePoints())
   */
  getData(): GamificationData {
    const data = this.loadData();
    if (!data) {
      return this.initialize();
    }
    
    // Don't auto-update streak here - let recordActivity() handle it
    // This prevents streak updates when just viewing data
    
    return data;
  }

  /**
   * Check if streak was protected (for UI notifications)
   */
  wasStreakProtected(): boolean {
    const data = this.getData();
    return data.streakProtected || false;
  }

  /**
   * Record study activity for streak (AI sessions, Practice Tests)
   * Does NOT award KP, only updates streak
   * Mock exams are excluded from streak
   */
  recordActivity(
    type: 'ai_session' | 'practice_test'
  ): { protected: boolean; streakIncreased: boolean; currentStreak: number } {
    const data = this.getData();
    
    // Update activity date
    const _previousDate = data.lastActivityDate;
    data.lastActivityDate = new Date().toISOString().split('T')[0];
    
    // Update streak (with automatic 1-day protection)
    const streakResult = this.updateStreak(data);
    
    this.saveData(data);
    
    logger.info(`Activity recorded: ${type}. Streak: ${data.dailyStudyStreak}`);
    
    return {
      protected: streakResult.protected,
      streakIncreased: streakResult.streakIncreased,
      currentStreak: data.dailyStudyStreak
    };
  }

  /**
   * Award Knowledge Points for an action
   * NOTE: Mock exams award KP but do NOT update streak
   */
  awardKnowledgePoints(
    type: 'correct_answer' | 'practice_test' | 'mock_exam' | 'daily_streak_bonus',
    count?: number
  ): { knowledgePoints: number; levelUp: boolean; newLevel?: number } {
    const data = this.getData();
    const kpAmount = this.KP_VALUES[type] * (count || 1);
    
    // Award Knowledge Points
    data.knowledgePoints += kpAmount;
    data.totalKnowledgePoints += kpAmount;
    
    // Check for level up
    const oldLevel = data.level;
    const newLevel = this.calculateLevel(data.totalKnowledgePoints);
    const levelUp = newLevel > oldLevel;
    
    if (levelUp) {
      data.level = newLevel;
      logger.info(`Level up! User reached level ${newLevel}`);
    }
    
    // Update activity date for streak ONLY if NOT a mock exam
    // Mock exams are excluded from streak
    if (type !== 'mock_exam' && type !== 'daily_streak_bonus') {
      data.lastActivityDate = new Date().toISOString().split('T')[0];
      // Update streak for practice tests
      this.updateStreak(data);
    }
    
    // Award daily streak bonus if applicable
    if (type !== 'daily_streak_bonus' && type !== 'mock_exam') {
      const streakBonus = this.checkStreakBonus(data);
      if (streakBonus > 0) {
        data.knowledgePoints += streakBonus;
        data.totalKnowledgePoints += streakBonus;
      }
    }
    
    this.saveData(data);
    
    // Log Knowledge Points transaction
    this.logKPTransaction({
      type,
      amount: kpAmount,
      timestamp: new Date().toISOString(),
      description: this.getKPDescription(type, kpAmount)
    });
    
    return {
      knowledgePoints: kpAmount,
      levelUp,
      newLevel: levelUp ? newLevel : undefined
    };
  }
  
  /**
   * Alias for backward compatibility (deprecated - use awardKnowledgePoints)
   */
  awardXP(
    type: 'correct_answer' | 'practice_test' | 'mock_exam' | 'daily_streak_bonus',
    count?: number
  ) {
    return this.awardKnowledgePoints(type, count);
  }

  /**
   * Update daily streak with automatic 1-day protection
   * All signed-in users have 1-day streak protection
   */
  private updateStreak(data: GamificationData): { protected: boolean; streakIncreased: boolean } {
    const today = new Date().toISOString().split('T')[0];
    const lastActivity = data.lastActivityDate;
    
    if (lastActivity === today) {
      // Already updated today
      return { protected: false, streakIncreased: false };
    }
    
    const lastDate = new Date(lastActivity);
    const todayDate = new Date(today);
    const daysDiff = Math.floor((todayDate.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24));
    
    // Reset protection flag
    data.streakProtected = false;
    
    // Check if streak is frozen (manual freeze)
    if (data.streakFrozen && daysDiff > 1) {
      // Use freeze - streak continues, unfreeze
      data.streakFrozen = false;
      data.streakFreezes = Math.max(0, data.streakFreezes - 1);
      data.dailyStudyStreak += 1;
      data.lastActivityDate = today;
      logger.info(`Streak frozen used! Streak continues at day ${data.dailyStudyStreak}`);
      this.saveData(data);
      return { protected: false, streakIncreased: true };
    }
    
    if (daysDiff === 1) {
      // Consecutive day - increment study streak
      data.dailyStudyStreak += 1;
      data.lastActivityDate = today;
      logger.info(`Study streak continued! Day ${data.dailyStudyStreak}`);
      this.saveData(data);
      return { protected: false, streakIncreased: true };
    } else if (daysDiff === 2) {
      // 1-day gap - automatic protection (all signed-in users get this)
      data.dailyStudyStreak += 1;
      data.lastActivityDate = today;
      data.streakProtected = true; // Mark as protected
      logger.info(`Streak protected! Day ${data.dailyStudyStreak} (1-day gap used)`);
      this.saveData(data);
      return { protected: true, streakIncreased: true };
    } else if (daysDiff > 2) {
      // More than 1 day missed - streak broken, reset
      data.dailyStudyStreak = 1;
      data.lastActivityDate = today;
      logger.info('Study streak reset - new streak started (more than 1 day missed)');
      this.saveData(data);
      return { protected: false, streakIncreased: false };
    }
    // daysDiff === 0 means same day, no update needed
    
    return { protected: false, streakIncreased: false };
  }

  /**
   * Check if user qualifies for daily study streak bonus
   */
  private checkStreakBonus(data: GamificationData): number {
    // Award bonus for study streaks of 3+ days
    if (data.dailyStudyStreak >= 3) {
      return this.KP_VALUES.daily_streak_bonus;
    }
    return 0;
  }

  /**
   * Freeze streak to prevent loss (if freezes available)
   */
  freezeStreak(): { success: boolean; message: string; freezesRemaining: number } {
    const data = this.getData();
    
    if (data.streakFrozen) {
      return {
        success: false,
        message: 'Streak is already frozen',
        freezesRemaining: data.streakFreezes
      };
    }
    
    if (data.streakFreezes <= 0) {
      return {
        success: false,
        message: 'No streak freezes available',
        freezesRemaining: 0
      };
    }
    
    data.streakFrozen = true;
    this.saveData(data);
    
    logger.info(`Streak frozen! Freezes remaining: ${data.streakFreezes - 1}`);
    
    return {
      success: true,
      message: 'Streak frozen! It will continue tomorrow',
      freezesRemaining: data.streakFreezes - 1
    };
  }

  /**
   * Get streak freeze status
   */
  getStreakFreezeStatus(): { freezesAvailable: number; isFrozen: boolean; canFreeze: boolean } {
    const data = this.getData();
    return {
      freezesAvailable: data.streakFreezes,
      isFrozen: data.streakFrozen,
      canFreeze: data.streakFreezes > 0 && !data.streakFrozen
    };
  }

  /**
   * Award free streak freeze (e.g., for achievements, level ups)
   */
  awardStreakFreeze(count: number = 1): void {
    const data = this.getData();
    data.streakFreezes += count;
    this.saveData(data);
    logger.info(`Awarded ${count} streak freeze(s). Total: ${data.streakFreezes}`);
  }

  /**
   * Calculate level from total Knowledge Points
   */
  private calculateLevel(totalKP: number): number {
    for (let i = this.LEVEL_THRESHOLDS.length - 1; i >= 0; i--) {
      if (totalKP >= this.LEVEL_THRESHOLDS[i]) {
        return i + 1;
      }
    }
    return 1;
  }

  /**
   * Get Knowledge Points needed for next level
   */
  getKPForNextLevel(): number {
    const data = this.getData();
    const currentLevel = data.level;
    
    if (currentLevel >= this.LEVEL_THRESHOLDS.length) {
      return 0; // Max level
    }
    
    const nextLevelThreshold = this.LEVEL_THRESHOLDS[currentLevel];
    return Math.max(0, nextLevelThreshold - data.totalKnowledgePoints);
  }

  /**
   * Get progress to next level (0-100%)
   */
  getLevelProgress(): number {
    const data = this.getData();
    const currentLevel = data.level;
    
    if (currentLevel >= this.LEVEL_THRESHOLDS.length) {
      return 100; // Max level
    }
    
    const currentThreshold = this.LEVEL_THRESHOLDS[currentLevel - 1] || 0;
    const nextThreshold = this.LEVEL_THRESHOLDS[currentLevel];
    const progress = data.totalKnowledgePoints - currentThreshold;
    const range = nextThreshold - currentThreshold;
    
    return Math.min(100, Math.max(0, (progress / range) * 100));
  }

  /**
   * Get level name/title
   */
  getLevelName(level: number): string {
    const levelNames: Record<number, string> = {
      1: 'Beginner Driver',
      2: 'Learning Driver',
      3: 'Confident Learner',
      4: 'Skilled Student',
      5: 'Proficient Driver',
      6: 'Advanced Learner',
      7: 'Expert Navigator',
      8: 'Master Driver',
      9: 'Elite Student',
      10: 'Champion Driver',
      11: 'Legendary Learner',
      12: 'Ultimate Navigator',
      13: 'Perfect Driver',
      14: 'Supreme Student',
      15: 'Grand Master',
      16: 'Elite Champion',
      17: 'Ultimate Master',
      18: 'Supreme Legend',
      19: 'Perfect Grand Master',
      20: '24-Hour Master'
    };
    
    return levelNames[level] || `Level ${level}`;
  }

  /**
   * Get Knowledge Points description for transaction
   */
  private getKPDescription(type: string, amount: number): string {
    const descriptions: Record<string, string> = {
      correct_answer: `+${amount} KP for correct answer`,
      practice_test: `+${amount} KP for completing practice test`,
      mock_exam: `+${amount} KP for completing mock exam`,
      daily_streak_bonus: `+${amount} KP daily study streak bonus`,
      level_up: 'Level up!'
    };
    
    return descriptions[type] || `+${amount} KP`;
  }

  /**
   * Log Knowledge Points transaction for history
   */
  private logKPTransaction(transaction: KnowledgePointsTransaction): void {
    try {
      const history = this.loadKPHistory();
      history.push(transaction);
      
      // Keep only last 100 transactions
      if (history.length > 100) {
        history.shift();
      }
      
      localStorage.setItem(this.KP_HISTORY_KEY, JSON.stringify(history));
    } catch (error) {
      logger.error('Error logging Knowledge Points transaction:', error);
    }
  }

  /**
   * Get Knowledge Points history
   */
  getKPHistory(): KnowledgePointsTransaction[] {
    return this.loadKPHistory();
  }

  /**
   * Load gamification data from storage
   */
  private loadData(): GamificationData | null {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return null;
      
      const data = JSON.parse(stored) as GamificationData;
      
      // Validate data structure - check for both old and new property names for migration
      const hasValidKP = typeof data.knowledgePoints === 'number' || typeof (data as any).xp === 'number';
      const hasValidLevel = typeof data.level === 'number';
      
      if (!hasValidKP || !hasValidLevel) {
        return null;
      }
      
      // Migrate old data format to new format if needed
      if (typeof (data as any).xp === 'number' && typeof data.knowledgePoints !== 'number') {
        data.knowledgePoints = (data as any).xp;
        data.totalKnowledgePoints = (data as any).totalXP || (data as any).xp;
        data.dailyStudyStreak = (data as any).dailyStreak || 0;
        delete (data as any).xp;
        delete (data as any).totalXP;
        delete (data as any).dailyStreak;
        this.saveData(data); // Save migrated data
      }
      
      // Ensure new fields exist
      if (typeof data.streakFreezes !== 'number') {
        data.streakFreezes = 1;
      }
      if (typeof data.streakFrozen !== 'boolean') {
        data.streakFrozen = false;
      }
      if (typeof data.streakProtected !== 'boolean') {
        data.streakProtected = false;
      }
      
      return data;
    } catch (error) {
      logger.error('Error loading gamification data:', error);
      return null;
    }
  }

  /**
   * Save gamification data to storage
   */
  private saveData(data: GamificationData): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      logger.error('Error saving gamification data:', error);
    }
  }

  /**
   * Load Knowledge Points history from storage
   */
  private loadKPHistory(): KnowledgePointsTransaction[] {
    try {
      const stored = localStorage.getItem(this.KP_HISTORY_KEY);
      if (!stored) return [];
      return JSON.parse(stored) as KnowledgePointsTransaction[];
    } catch (error) {
      logger.error('Error loading Knowledge Points history:', error);
      return [];
    }
  }

  /**
   * Reset gamification data (for testing/debugging)
   */
  reset(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem(this.KP_HISTORY_KEY);
    this.initialize();
  }
}

export const gamificationService = new GamificationService();
